import { Svg, G, Path, Circle, Text } from '@react-pdf/renderer';
import { buttonTrianglePath, elasticButtonStyle, placementColor } from '../clinical/elastics/model';
import type { ElasticPlacement } from '../clinical/elastics/model';
import {
    bandPathD,
    orderBadgePositions,
    placementArrowHeads,
    placementPoints
} from '../clinical/elastics/bandGeometry';
import { lingualToothSet } from '../clinical/elastics/persistence';
import {
    ODONTOGRAM_VIEWBOX,
    toothButtonAnchor,
    toothGlyph,
    toothLabel,
    toothLabelPos,
    toothLayout,
    toothOffset
} from '../clinical/elastics/toothGeometry';

interface OdontogramPdfProps {
    placements: ElasticPlacement[];
    /** Rendered width in PDF points; height derives from the viewBox ratio. */
    width?: number;
}

const TEETH = toothLayout();
const ASPECT = ODONTOGRAM_VIEWBOX.height / ODONTOGRAM_VIEWBOX.width;

/** Reuses the on-screen picker's geometry so the printed diagram matches what
 *  the doctor placed. */
export function OdontogramPdf({ placements, width = 480 }: OdontogramPdfProps) {
    const height = width * ASPECT;
    // One button per tooth that an elastic lands on (red = buccal, blue = lingual).
    const lingual = lingualToothSet(placements);
    const seen = new Set<number>();
    const buttonAnchors: Array<{ fdi: number; x: number; y: number; lingual: boolean }> = [];
    for (const placement of placements) {
        for (const fdi of placement.teeth) {
            if (seen.has(fdi)) continue;
            seen.add(fdi);
            buttonAnchors.push({ fdi, ...toothButtonAnchor(fdi), lingual: lingual.has(fdi) });
        }
    }
    const badgePositions = orderBadgePositions(placements);
    return (
        <Svg
            width={width}
            height={height}
            viewBox={`${ODONTOGRAM_VIEWBOX.minX} ${ODONTOGRAM_VIEWBOX.minY} ${ODONTOGRAM_VIEWBOX.width} ${ODONTOGRAM_VIEWBOX.height}`}
        >
            {/* Teeth */}
            <G>
                {TEETH.map((tooth) => {
                    const glyph = toothGlyph(tooth.fdi);
                    const label = toothLabelPos(tooth.fdi);
                    const { dx, dy } = toothOffset(tooth.fdi);
                    return (
                        <G key={tooth.fdi}>
                            <Path
                                d={glyph.outline}
                                transform={`translate(${dx}, ${dy})`}
                                fill="#ffffff"
                                stroke="#8b96a3"
                                strokeWidth={2.6}
                            />
                            <Text
                                x={label.x}
                                y={label.y + 5}
                                textAnchor="middle"
                                fill="#94a0ad"
                                style={{ fontSize: 16 }}
                            >
                                {toothLabel(tooth.fdi)}
                            </Text>
                        </G>
                    );
                })}
            </G>

            {/* Bands + order number over the teeth */}
            <G>
                {placements.map((placement, index) => {
                    const points = placementPoints(placement);
                    const color = placementColor(placement);
                    const d = bandPathD(points);
                    const arrows = placementArrowHeads(placement);
                    const centroid = badgePositions[index];
                    return (
                        <G key={placement.id}>
                            {d ? (
                                <Path
                                    d={d}
                                    stroke={color}
                                    strokeWidth={3.6}
                                    fill="none"
                                    strokeLinecap="round"
                                />
                            ) : null}
                            {arrows.map((head, i) => (
                                <Path key={`${placement.id}-arrow-${i}`} d={head} fill={color} />
                            ))}
                            <Circle
                                cx={centroid.x}
                                cy={centroid.y}
                                r={12.5}
                                fill={color}
                                stroke="#ffffff"
                                strokeWidth={2}
                            />
                            <Text
                                x={centroid.x}
                                y={centroid.y + 5}
                                textAnchor="middle"
                                fill="#ffffff"
                                style={{ fontSize: 15 }}
                            >
                                {String(index + 1)}
                            </Text>
                        </G>
                    );
                })}
            </G>

            {/* Shape, not just color, encodes the side (red circle buccal, blue
                triangle lingual) so it stays readable on a B&W printout. */}
            <G>
                {buttonAnchors.map((pt) => {
                    const style = elasticButtonStyle(pt.lingual ? 'lingual' : 'buccal');
                    if (style.shape === 'triangle') {
                        return (
                            <Path
                                key={pt.fdi}
                                d={buttonTrianglePath(pt.x, pt.y, style.radius)}
                                fill={style.fill}
                                stroke={style.stroke}
                                strokeWidth={style.strokeWidth}
                            />
                        );
                    }
                    return (
                        <Circle
                            key={pt.fdi}
                            cx={pt.x}
                            cy={pt.y}
                            r={style.radius}
                            fill={style.fill}
                            stroke={style.stroke}
                            strokeWidth={style.strokeWidth}
                        />
                    );
                })}
            </G>
        </Svg>
    );
}
